/**
 * Orientation rules for phones. The game is landscape only: the 800x600 stage
 * on a phone held upright is a short strip between two black bands, too small
 * to play. Pure logic, so the tests can import it; the DOM side is
 * src/modules/RotatePrompt.js.
 */

/**
 * Whether a window this size is held upright. A square counts as landscape:
 * the stage fits it as well as it fits any landscape window.
 * @param {number} width window width in CSS px
 * @param {number} height window height in CSS px
 * @returns {boolean}
 */
export function isPortrait(width, height) {
  return height > width;
}

/**
 * Whether to cover the game with "Turn your phone sideways to play". Only on a
 * touch device (a narrow desktop window is not a phone and keeps playing), only
 * while the game is on screen (from the first level card to the end panel,
 * which has its own portrait layout), and not after a quit.
 * @param {Object} s
 * @param {boolean} s.touch the primary pointer is coarse
 * @param {number} s.width window width
 * @param {number} s.height window height
 * @param {boolean} [s.panelOpen] the end panel is up
 * @param {boolean} [s.abandoned] the player went home
 * @returns {boolean}
 */
export function shouldPromptRotate({touch, width, height, panelOpen = false, abandoned = false}) {
  return !!touch && isPortrait(width, height) && !panelOpen && !abandoned;
}

/**
 * What the game pause should do when the prompt comes or goes. The prompt
 * pauses a running game and remembers that it did, so turning the phone back
 * resumes it; a game the player had already paused stays paused.
 * @param {Object} s
 * @param {boolean} s.prompt the prompt is (now) showing
 * @param {boolean} s.paused the game is paused
 * @param {boolean} s.heldForRotate the prompt is what paused it
 * @returns {{toggle: boolean, heldForRotate: boolean}} toggle: call the game's
 *   pause toggle once; heldForRotate: the new value to keep
 */
export function rotatePause({prompt, paused, heldForRotate}) {
  if (prompt) {
    return paused ? {toggle: false, heldForRotate} : {toggle: true, heldForRotate: true};
  }
  if (heldForRotate) {
    return {toggle: paused, heldForRotate: false};
  }
  return {toggle: false, heldForRotate: false};
}

/**
 * The rotate prompt's line: the picked dog is waiting, so it asks the player
 * to turn sideways to get that dog running. Without a known dog it just says
 * to turn sideways to play.
 * @param {string} [dogName] the picked dog's name, as in dogs.json
 * @returns {string} uppercase, as the card shows it
 */
export function rotateTitle(dogName) {
  const name = typeof dogName === 'string' ? dogName.trim() : '';
  return name ? `TURN SIDEWAYS TO GET ${name.toUpperCase()} RUNNING` : 'TURN SIDEWAYS TO PLAY';
}
